import type { AppState, Exercise, ExerciseCategory, ExerciseMetric, WorkoutTemplate } from '../domain/types'
import wgerExerciseData from './generated/wgerExercises.json'

const ex = (
  id: string,
  name: string,
  category: ExerciseCategory,
  equipment: string,
  restSeconds = 120,
  increment = 2.5,
  metrics: ExerciseMetric[] = ['weight', 'reps'],
): Exercise => ({ id, name, category, equipment, restSeconds, increment, metrics })

const coreExercises: Exercise[] = [
  ex('ring-muscle-up', 'Ring Muscle-Up', 'Anillas', 'Anillas', 180, 1, ['reps', 'weight', 'duration', 'assistance', 'success', 'technique']),
  ex('strict-ring-muscle-up', 'Strict Ring Muscle-Up', 'Anillas', 'Anillas', 180, 1, ['reps', 'weight', 'duration', 'assistance', 'success', 'technique']),
  ex('band-assisted-ring-muscle-up', 'Ring Muscle-Up con banda', 'Anillas', 'Anillas + banda', 150, 1, ['reps', 'assistance', 'success', 'technique']),
  ex('low-ring-transition', 'Transición de Muscle-Up en anillas bajas', 'Anillas', 'Anillas bajas', 90, 1, ['reps', 'duration', 'success', 'technique']),
  ex('false-grip-ring-pull-up', 'Dominada en anillas con false grip', 'Anillas', 'Anillas', 120, 1, ['reps', 'weight', 'assistance', 'duration']),
  ex('ring-chest-to-bar', 'Ring Chest-to-Bar Pull-Up', 'Anillas', 'Anillas', 120, 1, ['reps', 'weight', 'assistance']),
  ex('deep-ring-dip', 'Fondo profundo en anillas', 'Anillas', 'Anillas', 120, 1, ['reps', 'weight', 'assistance', 'technique']),
  ex('russian-ring-dip', 'Russian Dip en anillas', 'Anillas', 'Anillas', 120, 1, ['reps', 'assistance', 'success', 'technique']),
  ex('ring-support-hold', 'Soporte estable en anillas', 'Anillas', 'Anillas', 75, 1, ['duration', 'weight', 'technique']),
  ex('ring-turnout-hold', 'Ring Turn-Out Support Hold', 'Anillas', 'Anillas', 75, 1, ['duration', 'weight', 'technique']),
  ex('incline-press', 'Press inclinado 20–30°', 'Pecho', 'Barra', 150),
  ex('chest-press', 'Chest Press convergente', 'Pecho', 'Máquina', 120),
  ex('cable-lateral-raise', 'Elevación lateral en cable', 'Hombro', 'Cable', 75, 1),
  ex('single-arm-lat-pulldown', 'Jalón unilateral', 'Espalda', 'Cable', 105),
  ex('reverse-pec-deck', 'Reverse Pec Deck', 'Hombro', 'Máquina', 75),
  ex('overhead-triceps-extension', 'Extensión overhead de tríceps', 'Tríceps', 'Cable', 90),
  ex('incline-curl', 'Curl inclinado', 'Bíceps', 'Mancuernas', 90, 1),
  ex('cable-crunch', 'Cable Crunch', 'Abs', 'Cable', 75),
  ex('hanging-leg-raise', 'Hanging Leg Raise', 'Abs', 'Peso corporal', 75, 2.5, ['reps', 'weight']),
  ex('hack-squat', 'Hack Squat', 'Piernas', 'Máquina', 180, 5),
  ex('leg-press', 'Leg Press', 'Piernas', 'Máquina', 150, 5),
  ex('bulgarian-split-squat', 'Bulgarian Split Squat', 'Piernas', 'Mancuernas', 120),
  ex('leg-extension', 'Leg Extension', 'Piernas', 'Máquina', 90),
  ex('leg-curl', 'Leg Curl', 'Piernas', 'Máquina', 90),
  ex('calf-raise', 'Calf Raise', 'Piernas', 'Máquina', 90),
  ex('weighted-pull-up', 'Weighted Pull-Up / Ring Pull-Up', 'Espalda', 'Anillas', 150, 1, ['reps', 'weight', 'assistance']),
  ex('chest-supported-row', 'Chest Supported Row', 'Espalda', 'Máquina', 135),
  ex('preacher-curl', 'Preacher Curl', 'Bíceps', 'Máquina', 90),
  ex('hammer-curl', 'Hammer Curl', 'Bíceps', 'Mancuernas', 90, 1),
  ex('pushdown', 'Pushdown', 'Tríceps', 'Cable', 90),
  ex('ab-wheel', 'Ab Wheel', 'Abs', 'Peso corporal', 75, 2.5, ['reps', 'duration']),
  ex('romanian-deadlift', 'Romanian Deadlift', 'Piernas', 'Barra', 180),
  ex('hip-thrust', 'Hip Thrust', 'Piernas', 'Barra', 150, 5),
  ex('walking-lunge', 'Walking Lunge', 'Piernas', 'Mancuernas', 120),
  ex('adductor', 'Adductor Machine', 'Piernas', 'Máquina', 90),
  ex('incline-machine-press', 'Incline Machine Press', 'Pecho', 'Máquina', 135),
  ex('low-high-cable-fly', 'Low-to-High Cable Fly', 'Pecho', 'Cable', 90),
  ex('lat-pulldown', 'Lat Pulldown', 'Espalda', 'Cable', 105),
  ex('high-chest-supported-row', 'High Chest Supported Row', 'Espalda', 'Máquina', 105),
  ex('hyrox', 'HYROX / Cardio funcional', 'HYROX', 'Cardio', 60, 0, ['duration', 'success']),
]

type CatalogExercise = Exercise & { sourceId: number }
const catalogExercises = wgerExerciseData as CatalogExercise[]
const imageSourceByCoreExercise: Record<string, number> = {
  'incline-press': 538,
  'chest-press': 129,
  'cable-lateral-raise': 1378,
  'overhead-triceps-extension': 1519,
  'hack-squat': 375,
  'leg-press': 371,
  'bulgarian-split-squat': 1706,
  'leg-extension': 369,
  'leg-curl': 366,
  'calf-raise': 622,
  'preacher-curl': 465,
  'hammer-curl': 272,
  pushdown: 805,
  'walking-lunge': 206,
  adductor: 12,
  'lat-pulldown': 1136,
}
const mappedSourceIds = new Set(Object.values(imageSourceByCoreExercise))
const coreDescriptions: Record<string, string> = {
  'ring-muscle-up': 'Movimiento compuesto en anillas: tirón explosivo con false grip, transición del torso por encima de las anillas y fondo hasta el soporte. No es una dominada; la transición y el empuje forman parte de cada repetición.',
  'strict-ring-muscle-up': 'Muscle-up sin impulso: false grip, tirón alto, transición cerrada y fondo estable, manteniendo las anillas cerca del cuerpo.',
  'band-assisted-ring-muscle-up': 'Muscle-up completo con una banda que reduce la demanda. Conserva el tirón, la transición y el fondo del movimiento real.',
  'low-ring-transition': 'Desde pies apoyados y anillas bajas, practica el paso del tirón al fondo llevando el pecho sobre las manos con las anillas pegadas al torso.',
}

export const exercises: Exercise[] = [
  ...coreExercises.map((exercise) => {
    const sourceId = imageSourceByCoreExercise[exercise.id]
    const source = catalogExercises.find((item) => item.sourceId === sourceId)
    return source ? {
      ...exercise,
      imagePath: source.imagePath,
      imageAttribution: source.imageAttribution,
      description: source.description,
      videoUrl: source.videoUrl,
    } : { ...exercise, description: coreDescriptions[exercise.id] }
  }),
  ...catalogExercises
    .filter((exercise) => !mappedSourceIds.has(exercise.sourceId))
    .map((exercise) => ({ ...exercise, imagePath: undefined, imageAttribution: undefined })),
]

type Entry = [string, number, number, number, number, string, string, ('reps' | 'min')?]
const template = (id: string, name: string, fullName: string, weekday: number, minutes: number, focus: string, entries: Entry[]): WorkoutTemplate => ({
  id,
  name,
  fullName,
  weekday,
  minutes,
  focus,
  entries: entries.map(([exerciseId, sets, minReps, maxReps, restSeconds, targetRir, cue, repUnit]) => ({ exerciseId, sets, minReps, maxReps, restSeconds, targetRir, cue, repUnit })),
})

export const templates: WorkoutTemplate[] = [
  template('upper-a', 'Upper A', 'Lunes — Upper A', 1, 74, 'Muscle-up · pecho superior · deltoides · abs', [
    ['ring-muscle-up', 4, 2, 4, 180, '2', 'Rep limpia; cero grind. Prioridad técnica.'],
    ['incline-press', 4, 6, 10, 150, '1–2', 'Escápulas estables; pausa corta abajo.'],
    ['chest-press', 3, 8, 12, 120, '1', 'Control del estiramiento; no rebotes.'],
    ['cable-lateral-raise', 4, 10, 15, 75, '0–1', 'Cable detrás del cuerpo; lidera con el codo.'],
    ['single-arm-lat-pulldown', 3, 8, 12, 105, '1', 'Codo hacia cadera; torso estable.'],
    ['reverse-pec-deck', 3, 12, 20, 75, '0–1', 'Deltoide posterior; sin encoger trapecio.'],
    ['overhead-triceps-extension', 3, 8, 12, 90, '0–1', 'Estiramiento largo; codos fijos.'],
    ['incline-curl', 3, 8, 12, 90, '0–1', 'Hombro atrás; supinación completa.'],
    ['cable-crunch', 4, 8, 15, 75, '0–1', 'Flexiona columna; pelvis estable.'],
    ['hanging-leg-raise', 3, 8, 15, 75, '1', 'Retroversión pélvica; sin balanceo.'],
  ]),
  template('lower-a', 'Lower A', 'Martes — Lower A', 2, 62, 'Cuádriceps · unilateral · pantorrilla', [
    ['hack-squat', 4, 6, 10, 180, '1–2', 'Profundidad consistente; rodilla viaja libre.'],
    ['leg-press', 3, 8, 12, 150, '1', 'ROM largo sin despegar cadera.'],
    ['bulgarian-split-squat', 3, 8, 12, 120, '1', 'Por lado; paso estable y control abajo.'],
    ['leg-extension', 3, 10, 15, 90, '0–1', 'Aprieta arriba 1 segundo.'],
    ['leg-curl', 3, 10, 15, 90, '0–1', 'Cadera pegada; controla excéntrica.'],
    ['calf-raise', 4, 8, 15, 90, '0–1', 'Pausa estirada abajo y contracción arriba.'],
  ]),
  template('upper-b', 'Upper B', 'Miércoles — Upper B', 3, 80, 'Muscle-up · espalda · brazos · abs', [
    ['ring-muscle-up', 4, 2, 5, 180, '2', 'Misma técnica; acumula reps perfectas.'],
    ['weighted-pull-up', 4, 5, 8, 150, '1–2', 'Pecho alto; rango completo.'],
    ['chest-supported-row', 4, 6, 10, 135, '1', 'Sin impulso; codos 30–60°.'],
    ['single-arm-lat-pulldown', 3, 8, 12, 105, '1', 'Énfasis dorsal; codo a cadera.'],
    ['reverse-pec-deck', 3, 12, 20, 75, '0–1', 'Control estricto.'],
    ['cable-lateral-raise', 4, 10, 15, 75, '0–1', 'Sin balanceo; tensión constante.'],
    ['preacher-curl', 3, 8, 12, 90, '0–1', 'No pierdas tensión abajo.'],
    ['hammer-curl', 3, 8, 12, 90, '0–1', 'Muñeca neutra; braquial.'],
    ['pushdown', 3, 8, 12, 90, '0–1', 'Bloqueo fuerte; hombro quieto.'],
    ['cable-crunch', 4, 8, 15, 75, '0–1', 'Progresión con carga.'],
    ['ab-wheel', 3, 8, 15, 75, '1', 'Costillas abajo; no hiperextender lumbar.'],
  ]),
  template('lower-b', 'Lower B', 'Jueves — Lower B', 4, 61, 'Femoral · glúteo · unilateral', [
    ['romanian-deadlift', 4, 6, 10, 180, '1–2', 'Cadera atrás; femoral cargado.'],
    ['hip-thrust', 3, 6, 10, 150, '1', 'Bloqueo completo sin hiperextender.'],
    ['leg-curl', 4, 8, 12, 105, '0–1', 'Excéntrica lenta.'],
    ['walking-lunge', 3, 10, 14, 120, '1', 'Por lado; zancada estable y torso controlado.'],
    ['adductor', 3, 10, 15, 90, '0–1', 'ROM cómodo y controlado.'],
    ['calf-raise', 4, 8, 15, 90, '0–1', 'Misma técnica del martes.'],
  ]),
  template('upper-c', 'Upper C', 'Viernes — Upper C', 5, 77, 'Estética total · deltoide lateral · brazos · abs', [
    ['incline-machine-press', 3, 8, 12, 135, '1', 'Pecho superior; recorrido estable.'],
    ['low-high-cable-fly', 3, 10, 15, 90, '0–1', 'Cruza ligeramente; sin encoger hombros.'],
    ['lat-pulldown', 3, 8, 12, 105, '1', 'Depresión escapular antes de tirar.'],
    ['high-chest-supported-row', 3, 8, 12, 105, '1', 'Espalda alta; pausa atrás.'],
    ['cable-lateral-raise', 5, 10, 20, 60, '0–1', 'Volumen prioritario de deltoide lateral.'],
    ['reverse-pec-deck', 3, 15, 20, 60, '0–1', 'Bombeo controlado.'],
    ['incline-curl', 3, 8, 12, 90, '0–1', 'Estiramiento largo.'],
    ['hammer-curl', 2, 10, 15, 75, '0–1', 'Sin balanceo.'],
    ['overhead-triceps-extension', 3, 8, 12, 90, '0–1', 'Cabeza larga del tríceps.'],
    ['pushdown', 2, 10, 15, 75, '0–1', 'Finaliza con control.'],
    ['cable-crunch', 4, 10, 15, 75, '0–1', 'Abs pesados.'],
    ['hanging-leg-raise', 3, 10, 15, 75, '1', 'Pelvis hacia costillas.'],
  ]),
  template('hyrox-day', 'HYROX', 'Sábado — HYROX', 6, 55, 'Condición · técnica · capacidad aeróbica', [
    ['hyrox', 1, 25, 90, 0, 'Variable', 'Elige base aeróbica, técnica, umbral o simulación según recuperación.', 'min'],
  ]),
]

export const initialState: AppState = {
  updatedAt: '',
  exercises,
  templates,
  sessions: [],
  measurements: [],
  preferences: { unit: 'metric', restSound: true, loadMemoryWeeks: 0, overloadEveryWeeks: 4, overloadStartedOn: '' },
  profile: {
    name: 'Jose',
    age: 21,
    heightCm: 184,
    dietStartedOn: '',
    phase: 'definition',
    nutritionTargets: { calories: 2350, protein: 176, carbohydrates: 264, fat: 65 },
    onboardingComplete: false,
  },
  dietCheckIns: [],
  coachMessages: [],
}
